import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { detectAdRequired } from "@/lib/ads";
import RewardedAdModal from "@/components/RewardedAdModal";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Hand, Upload, Camera, Sparkles, RefreshCw, History, Lock, ChevronRight, PlayCircle } from "lucide-react";
import { toast } from "sonner";

const MAX_SIZE = 6 * 1024 * 1024; // 6MB pre-compression

/** Downscale + JPEG-compress the palm photo entirely in the browser so we
 * only ship ~200-400 KB to Claude vision. */
async function preparePalmImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const maxDim = 1400;
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const scale = Math.min(maxDim / width, maxDim / height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
        const base64 = dataUrl.split(",")[1];
        resolve({ base64, previewUrl: dataUrl });
      };
      img.onerror = () => reject(new Error("Could not decode image"));
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function ReportSection({ title, entries, testId }) {
  if (!entries) return null;
  const items = Object.entries(entries).filter(([, v]) => v);
  if (items.length === 0) return null;
  return (
    <div className="glass rounded-2xl p-6 border border-white/5" data-testid={testId}>
      <h3 className="font-serif text-xl text-amber-300 mb-4">{title}</h3>
      <div className="space-y-3">
        {items.map(([k, v]) => (
          <div key={k}>
            <div className="text-xs uppercase tracking-widest text-amber-400/80 mb-1">{k.replace(/_/g, " ")}</div>
            <p className="text-sm text-slate-300 leading-relaxed">{v}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function BulletList({ title, items, testId }) {
  if (!items?.length) return null;
  return (
    <div className="glass rounded-2xl p-6 border border-white/5" data-testid={testId}>
      <h3 className="font-serif text-xl text-amber-300 mb-4">{title}</h3>
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={i} className="text-sm text-slate-300 leading-relaxed flex gap-2">
            <ChevronRight className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function PalmReading() {
  const { t, lang } = useI18n();
  const { user, checkAuth } = useAuth();
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [reading, setReading] = useState(null);
  const [quota, setQuota] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [adOpen, setAdOpen] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const fetchQuota = async () => {
    try {
      const { data } = await api.get("/palm/quota");
      setQuota(data);
    } catch { /* noop */ }
  };
  const fetchHistory = async () => {
    try {
      const { data } = await api.get("/palm/history");
      setHistory(data.items || []);
    } catch { /* noop */ }
  };

  useEffect(() => {
    fetchQuota();
    fetchHistory();
  }, []);

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // Accept any image/* — we transcode to JPEG in preparePalmImage anyway.
    if (!f.type?.startsWith("image/")) {
      toast.error(t("palm.err_type"));
      return;
    }
    if (f.size > MAX_SIZE) {
      toast.error(t("palm.err_large"));
      return;
    }
    setFile(f);
    setReading(null);
    try {
      const { previewUrl: p } = await preparePalmImage(f);
      setPreviewUrl(p);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const submit = async () => {
    if (!file) return;
    setProcessing(true);
    setReading(null);
    try {
      const { base64 } = await preparePalmImage(file);
      const { data } = await api.post("/palm/read", { image_base64: base64, language: lang });
      setReading(data.reading);
      setQuota((q) => ({ ...(q || {}), ...(data.quota || {}) }));
      fetchHistory();
      toast.success(t("palm.success"));
    } catch (err) {
      if (detectAdRequired(err)) {
        setAdOpen(true);
        toast.info("Watch a short ad to continue.");
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setProcessing(false);
    }
  };

  const onAdRewarded = async () => {
    await fetchQuota();
    checkAuth?.();
    toast.success("Unlocked! Ready for your next reading.");
  };

  const inrPrice = 0; // legacy fields kept for compatibility
  const usdPrice = 0;
  const canRead = quota?.unlimited || (quota?.actions_remaining ?? 0) > 0;

  return (
    <div className="space-y-8 max-w-6xl mx-auto" data-testid="palm-reading-page">
      <section className="glass rounded-2xl p-8">
        <div className="flex items-center gap-3 mb-2">
          <Hand className="w-6 h-6 text-gold" />
          <h1 className="font-serif text-3xl text-slate-50">{t("palm.title")}</h1>
        </div>
        <p className="text-slate-400 max-w-2xl">{t("palm.subtitle")}</p>

        {quota && (
          <div className="mt-4 flex flex-wrap gap-2 items-center text-sm" data-testid="palm-quota">
            {quota.unlimited ? (
              <span className="px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-300">Unlimited (premium)</span>
            ) : (quota.actions_remaining || 0) > 0 ? (
              <span className="px-3 py-1 rounded-full bg-amber-500/10 text-amber-300">{quota.actions_remaining} free readings left</span>
            ) : (
              <span className="px-3 py-1 rounded-full bg-rose-500/10 text-rose-300 inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Watch an ad to unlock more</span>
            )}
          </div>
        )}
      </section>

      {!canRead && quota && !quota.unlimited && (
        <section className="glass rounded-2xl p-6 border border-amber-500/20" data-testid="palm-paywall">
          <h2 className="font-serif text-2xl text-slate-50 mb-1">Unlock more readings</h2>
          <p className="text-slate-400 mb-4 text-sm">Watch a short ad to unlock 3 more premium features — palm readings, kundli matching, lo-shu, deep analysis and more.</p>
          <Button
            onClick={() => setAdOpen(true)}
            data-testid="palm-watch-ad-btn"
            className="bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-full"
          >
            <PlayCircle className="w-4 h-4 mr-2" /> Watch Ad to Unlock
          </Button>
        </section>
      )}

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="glass border-white/10 p-6">
          <h2 className="font-serif text-xl text-slate-50 mb-3">{t("palm.upload_title")}</h2>
          <div
            onClick={() => canRead && fileRef.current?.click()}
            className={`border-2 border-dashed rounded-2xl p-8 text-center transition ${
              canRead ? "border-white/10 hover:border-amber-500/40 cursor-pointer" : "border-white/5 opacity-50 cursor-not-allowed"
            }`}
            data-testid="palm-upload-zone"
          >
            {previewUrl ? (
              <img src={previewUrl} alt="Palm preview" className="mx-auto max-h-64 rounded-xl" data-testid="palm-preview" />
            ) : (
              <>
                <Upload className="w-10 h-10 text-amber-400 mx-auto mb-3" />
                <p className="text-slate-300 mb-1">{t("palm.upload_cta")}</p>
                <p className="text-xs text-slate-500">{t("palm.upload_hint")}</p>
              </>
            )}
          </div>
          {/* Two hidden inputs: one opens the device camera on mobile
              (capture=environment triggers rear camera), the other picks
              from the gallery. Desktop browsers ignore `capture` and show
              the normal file chooser. */}
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={onPickFile}
            data-testid="palm-camera-input"
          />
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={onPickFile}
            data-testid="palm-file-input"
          />
          <div className="grid grid-cols-2 gap-2 mt-4">
            <Button
              type="button"
              onClick={() => canRead && cameraRef.current?.click()}
              disabled={!canRead || processing}
              data-testid="palm-take-photo-btn"
              className="bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-full"
            >
              <Camera className="w-4 h-4 mr-2" /> {t("palm.take_photo")}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => canRead && fileRef.current?.click()}
              disabled={!canRead || processing}
              data-testid="palm-gallery-btn"
              className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 rounded-full"
            >
              <Upload className="w-4 h-4 mr-2" /> {t("palm.pick_gallery")}
            </Button>
          </div>
          <div className="flex gap-2 mt-3">
            <Button
              onClick={submit}
              disabled={!file || processing || !canRead}
              data-testid="palm-submit-btn"
              className="bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-full flex-1"
            >
              {processing ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> {t("palm.reading")}</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" /> {t("palm.read_btn")}</>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setFile(null); setPreviewUrl(null); setReading(null);
                if (fileRef.current) fileRef.current.value = "";
                if (cameraRef.current) cameraRef.current.value = "";
              }}
              disabled={processing || (!file && !reading)}
              className="border-white/10 text-slate-300 hover:bg-white/5 rounded-full"
              data-testid="palm-reset-btn"
            >
              {t("common.reset")}
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-4 leading-relaxed">{t("palm.tips")}</p>
        </Card>

        <Card className="glass border-white/10 p-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-serif text-xl text-slate-50">{t("palm.history_title")}</h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowHistory((v) => !v)}
              data-testid="palm-history-toggle"
              className="text-slate-400 hover:text-amber-300"
            >
              <History className="w-4 h-4 mr-1" /> {showHistory ? t("common.hide") : t("common.show")}
            </Button>
          </div>
          {showHistory ? (
            history.length === 0 ? (
              <p className="text-sm text-slate-500 italic">{t("palm.no_history")}</p>
            ) : (
              <div className="space-y-2 max-h-80 overflow-auto">
                {history.map((h) => (
                  <button
                    key={h.reading_id}
                    onClick={() => setReading(h)}
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/5 border border-white/5"
                    data-testid={`palm-history-item-${h.reading_id}`}
                  >
                    <div className="text-sm text-slate-200">{new Date(h.created_at).toLocaleString()}</div>
                    <div className="text-xs text-slate-500 truncate mt-0.5">{h.report?.overall_summary?.slice(0, 100)}...</div>
                  </button>
                ))}
              </div>
            )
          ) : (
            <p className="text-sm text-slate-400">{t("palm.history_hint")}</p>
          )}
        </Card>
      </section>

      {reading && (
        <section className="space-y-6" data-testid="palm-report">
          <div className="glass rounded-2xl p-6 border-l-4 border-amber-500">
            <div className="text-xs uppercase tracking-widest text-amber-300 mb-2">{t("palm.overall")}</div>
            <p className="text-slate-200 leading-relaxed font-serif text-lg">{reading.report?.overall_summary || reading.overall_summary}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ReportSection title={t("palm.lines")} entries={reading.report?.lines || reading.lines} testId="palm-lines" />
            <ReportSection title={t("palm.mounts")} entries={reading.report?.mounts || reading.mounts} testId="palm-mounts" />
          </div>

          <ReportSection
            title={t("palm.year_forecast")}
            entries={reading.report?.current_year_forecast_2026 || reading.current_year_forecast_2026}
            testId="palm-year"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <BulletList title={t("palm.lucky_periods")} items={reading.report?.lucky_periods || reading.lucky_periods} testId="palm-lucky" />
            <BulletList title={t("palm.remedies")} items={reading.report?.remedies || reading.remedies} testId="palm-remedies" />
          </div>
        </section>
      )}

      <RewardedAdModal
        open={adOpen}
        onClose={() => setAdOpen(false)}
        onRewarded={onAdRewarded}
        kind="generic"
        title="Unlock 3 more premium readings"
        description="Play a 5-second ad to reset your free readings for palm, kundli matching, lo-shu, dasha timeline, and more."
      />
    </div>
  );
}
